/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, observable } from 'mobx';
import { AppType } from 'src/core/stores/app/types';
import { LuisConstants } from 'src/core/utils/LuisConstants';

export type AppData = Pick<
    AppStore,
    'culture' | 'createdDate' | 'name' | 'description' | 'csAccountPath' | 'modifiedDate' | 'lastTrainedDate' | 'lastPublishedDate'
>;

export class AppStore {
    public readonly id: string;

    public readonly culture: string;

    public readonly appType: AppType;

    public readonly createdDate: Date;

    @observable public name: string;

    @observable public description: string;

    @observable public csAccountPath: string;

    @observable public modifiedDate: Date;

    @observable public lastTrainedDate: Date;

    @observable public lastPublishedDate: Date;

    constructor(id: string, appType: AppType, appData: Partial<AppData>) {
        this.id = id;
        this.appType = appType;
        this.culture = appData.culture;
        this.createdDate = appData.createdDate;

        this.name = appData.name || '';
        this.description = appData.description || '';
        this.csAccountPath = appData.csAccountPath || '';

        this.modifiedDate = appData.modifiedDate;
        this.lastTrainedDate = appData.lastTrainedDate;
        this.lastPublishedDate = appData.lastPublishedDate;
    }

    public get isRTL(): boolean {
        return LuisConstants.CultureSettings.RTLCultures.includes(this.culture);
    }

    @action
    public setDescription(description: string) {
        this.description = description;
    }

    @action
    public setModifiedDate(modifiedDate: Date) {
        this.modifiedDate = modifiedDate;
    }

    @action
    public setLastPublishedDate(lastPublishedDate: Date) {
        this.lastPublishedDate = lastPublishedDate;
    }

    @action
    public setLastTrainedDate(lastTrainedDate: Date) {
        this.lastTrainedDate = lastTrainedDate;
    }

    @action
    public setCsAccountPath(csAccountPath: string): void {
        this.csAccountPath = csAccountPath;
    }
}
